import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Images, LayoutTemplate, LucideIcon, Sparkles } from "lucide-react";
import { TabButton } from "@/containers/Coach/Reputation/components/ReputationHeader";
import { cn } from "@/lib/utils";

export interface PromptStudioHeaderTab {
  key: string;
  title: string;
  icon: LucideIcon;
}

/** Designs | Backgrounds, the two tabs of the Prompt Studio list pages (coach and admin). */
export const PROMPT_STUDIO_TABS: PromptStudioHeaderTab[] = [
  { key: "designs", title: "Designs", icon: LayoutTemplate },
  { key: "backgrounds", title: "Backgrounds", icon: Images },
];

export interface PromptStudioHeaderProps {
  /** Pages inside Prompt Studio (a design, New design) link back instead of showing the title. */
  backTo?: string;
  /** The back link's text; the section's own name unless the page came from somewhere else. */
  backLabel?: string;
  tabs?: PromptStudioHeaderTab[];
  selectedTab?: string;
  onTabChange?(key: string): void;
  /** What the page is about, after the divider (a design's name and status, "New design"). */
  children?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  testId?: string;
}

/**
 * The Prompt Studio section header, in the Reputation Manager style, shared by the design list, New
 * Design, and the editor so moving between them feels like one place.
 */
export const PromptStudioHeader: React.FC<PromptStudioHeaderProps> = ({
  backTo,
  backLabel = "Prompt Studio",
  tabs,
  selectedTab,
  onTabChange,
  children,
  actions,
  className,
  testId = "prompt-studio-header",
}) => (
  <header
    className={cn(
      "flex w-full shrink-0 flex-row flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-4 pt-4 pb-2",
      className
    )}
    data-testid={testId}
  >
    {/* min-h-9 keeps every page's header the height of a row of buttons, with or without them. */}
    <div className="flex min-h-9 min-w-0 flex-row flex-wrap items-center gap-4">
      {backTo ? (
        <Link
          to={backTo}
          className="group -ml-2 flex select-none flex-row items-center gap-1.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-50"
        >
          <ChevronLeft
            className="size-4 text-slate-400 transition-colors group-hover:text-primary"
            aria-hidden="true"
          />
          <span className="text-base font-bold text-slate-600 transition-colors group-hover:text-primary">
            {backLabel}
          </span>
        </Link>
      ) : (
        <div className="flex flex-row items-center gap-2">
          <div className="rounded-lg bg-indigo-50/50 p-1.5 text-primary">
            <Sparkles className="size-4" aria-hidden="true" />
          </div>
          <div className="text-xl font-bold tracking-tight text-slate-800">Prompt Studio</div>
        </div>
      )}

      {children && (
        <div className="ml-2 flex min-w-0 flex-row flex-wrap items-center gap-2 border-l-2 border-slate-100 pl-4">
          {children}
        </div>
      )}

      {tabs && tabs.length > 0 && (
        <div className="ml-2 flex flex-row items-center gap-1.5 border-l-2 border-slate-100 pl-4">
          {tabs.map(tab => (
            <TabButton
              key={tab.key}
              label={tab.title}
              icon={tab.icon}
              selected={selectedTab === tab.key}
              onClick={() => onTabChange?.(tab.key)}
            />
          ))}
        </div>
      )}
    </div>

    {actions && <div className="flex min-h-9 flex-row items-center gap-2">{actions}</div>}
  </header>
);
